/**
 * TrusteesCard — trustees list, driven by the trust's CANONICAL trustee roster
 * (`GET /v1/trusts/{id}/trustees` via useTrustTrustees — the SAME source the
 * trust Overview and People & Access screens read), filtered to active.
 *
 * Grader-r3 finding 4: this card used to count beneficial-ownership records
 * filtered to category='trustee'. A document-recognised trustee who has no BO
 * record yet (their ID hasn't been uploaded — the normal onboarding state)
 * showed here as "0 active → No trustees on file yet" while the trust's own
 * Overview / People & Access showed the real roster ("1 trustee") — two live
 * screens disagreeing on the same trust at the same moment. The card's copy
 * ("Trustees", "N active", "No trustees on file yet") is about the ROSTER, so
 * it now reads the roster; the BO register keeps its own surfaces.
 */

import { Link } from '@tanstack/react-router';
import { displayPersonName } from '../../lib/format/person-name';
import { useMe, useTrusts, useTrustTrustees } from '../../lib/api';
import { deedRoleLabel } from '../../lib/labels';
import { mapError } from '../../lib/error-messages';
import { initialsOf } from '../../lib/format/initials';

interface AvatarProps {
  initials: string;
  color: string;
  size?: number;
}

function Avatar({ initials, color, size = 36 }: AvatarProps) {
  return (
    <div
      className={'avatar ' + color}
      style={{ width: size, height: size, fontSize: size <= 30 ? 11 : 13 }}
    >
      {initials}
    </div>
  );
}

function colorOf(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const palette = ['teal-soft', 'ink', 'brass', ''];
  return palette[Math.abs(h) % palette.length] ?? 'teal-soft';
}

export function TrusteesCard() {
  const trustsQ = useTrusts();
  const trustId = trustsQ.data?.trusts[0]?.id;
  const rosterQ = useTrustTrustees(trustId);
  // The signed-in person. Under act-as this is still the firm user who signed
  // in, never the client being acted for, so "You" can only land on a row that
  // really is them.
  const myUserId = useMe().data?.user.supabaseUserId;

  // The canonical roster, ACTIVE members only (matching the "N active" copy and
  // the Overview/People screens' own filter), in the roster's display order.
  const trustees = (rosterQ.data?.trustees ?? [])
    .filter((t) => t.status === 'active')
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <div className="eyebrow">Held in steady hands</div>
          <h2 className="card-title">Trustees</h2>
        </div>
        {trustId && (
          <Link
            to={`/app/trusts/${encodeURIComponent(trustId)}/settings` as never}
            className="card-meta"
            style={{ textDecoration: 'none', color: 'var(--ink-3)', fontWeight: 400 }}
          >
            {trustees.length} active →
          </Link>
        )}
      </div>
      {rosterQ.isLoading ? (
        <p style={{ color: 'var(--ink-3)', fontSize: 13 }} aria-live="polite">
          Loading trustees on this trust&hellip;
        </p>
      ) : trustsQ.isError || rosterQ.isError ? (
        // A fetch failure must read as an ERROR, not "no trustees" — the
        // empty-state copy below would silently lie about the roster.
        <div role="alert" data-testid="trustees-error">
          <p style={{ color: 'var(--negative)', fontSize: 13, lineHeight: 1.55, margin: '0 0 8px' }}>
            Couldn&rsquo;t load trustees. {mapError(trustsQ.error ?? rosterQ.error, 'load')}
          </p>
          <button
            type="button"
            onClick={() => {
              if (trustsQ.isError) void trustsQ.refetch();
              if (rosterQ.isError) void rosterQ.refetch();
            }}
            style={{
              background: 'transparent',
              color: 'var(--primary-700)',
              border: '1px solid var(--primary-200, #b8d4ce)',
              padding: '5px 14px',
              borderRadius: 8,
              fontSize: 12.5,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      ) : trustees.length === 0 ? (
        <p style={{ color: 'var(--ink-3)', fontSize: 13, lineHeight: 1.55 }}>
          No trustees on file yet. Add the first one to begin the
          board structure.
        </p>
      ) : (
        <div className="trustee-list">
          {trustees.slice(0, 5).map((t) => (
            <Link
              key={t.id}
              to={
                trustId
                  ? (`/app/trusts/${encodeURIComponent(trustId)}/settings` as never)
                  : ('/app/trusts' as never)
              }
              className="trustee"
              style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 12 }}
            >
              <Avatar initials={initialsOf(t.displayName)} color={colorOf(t.id)} size={40} />
              <div style={{ flex: 1 }}>
                <div className="trustee-name">{displayPersonName(t.displayName)}</div>
                <div className="trustee-role">{deedRoleLabel(t.role) || 'Trustee'}</div>
                {/* Standing against the CURRENT Letter of Authority (Beno,
                    2026-08-01 — he holds three letters and everyone across all
                    three read as current). Shown only when the platform has a
                    letter to judge against, and deliberately silent when the
                    row IS on the letter: confirming the expected case adds
                    noise. 'undetermined' says the letter's initials could not
                    be matched with confidence, NOT that the person is absent. */}
                {t.loaStanding === 'not-on-current-letter' ? (
                  <div
                    data-testid={`loa-standing-${t.id}`}
                    style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}
                  >
                    Not named on the current letter of authority
                  </div>
                ) : t.loaStanding === 'undetermined' ? (
                  <div
                    data-testid={`loa-standing-${t.id}`}
                    style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}
                  >
                    Could not be matched to the current letter of authority
                  </div>
                ) : null}
              </div>
              {(() => {
                // The tag says what the role line does not. The founder's row is
                // STORED with tag "You" when the trust is created, so the stored
                // tag cannot be printed as is: "You" is computed here from the
                // signed-in person (NEW-R12-DASH-TRUSTEE-YOU), and a tag that
                // only repeats the role ("Trustee / Trustee") is not shown.
                const roleLine = deedRoleLabel(t.role) || 'Trustee';
                const tag =
                  !!t.userId && t.userId === myUserId
                    ? 'You'
                    : t.tag && t.tag !== 'You'
                      ? deedRoleLabel(t.tag)
                      : '';
                return tag && tag !== roleLine ? (
                  <span className="role-tag teal" data-testid={`trustee-tag-${t.id}`}>
                    {tag}
                  </span>
                ) : null;
              })()}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
